import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Suggestion } from '@/lib/suggestions/types';
import { cn } from '@/lib/utils';
import { Music } from 'lucide-react';
import { searchClient } from '@/lib/search/instantsearch/algoliaAdapter';

interface AlgoliaSearchAutocompleteProps {
  placeholder: string;
  icon: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  getSuggestionsAsync: (query: string) => Promise<Suggestion[]>;
  onSelectSuggestion: (suggestion: Suggestion) => void;
  className?: string;
  inputClassName?: string;
  dropdownClassName?: string;
  indexName?: 'sequences' | 'displays';
}

export const AlgoliaSearchAutocomplete: React.FC<AlgoliaSearchAutocompleteProps> = ({
  placeholder,
  icon,
  value,
  onChange,
  onSubmit,
  getSuggestionsAsync,
  onSelectSuggestion,
  className = '',
  inputClassName = '',
  dropdownClassName = '',
  indexName = 'sequences',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced async suggestion fetch
  useEffect(() => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const fetchSuggestions = async () => {
      if (value.length === 0) {
        // Show default suggestions when empty
        setIsLoading(true);
        const results = await getSuggestionsAsync('');
        setSuggestions(results);
        setTotalCount(0);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      abortControllerRef.current = new AbortController();

      try {
        // Fetch suggestions
        const results = await getSuggestionsAsync(value);
        setSuggestions(results);
        setIsOpen(results.length > 0);

        // Fetch total count for hybrid footer
        if (searchClient && value.trim().length >= 2) {
          const { results: countResults } = await searchClient.search([
            {
              indexName,
              query: value.trim(),
              params: {
                hitsPerPage: 0, // We only need the count
                attributesToRetrieve: [],
              },
            },
          ]);
          setTotalCount(countResults[0]?.nbHits || 0);
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Autocomplete error:', error);
          setSuggestions([]);
          setTotalCount(0);
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Debounce the request
    const timeoutId = setTimeout(fetchSuggestions, 300);

    return () => {
      clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [value, getSuggestionsAsync, indexName]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [suggestions]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown') {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[highlightedIndex]);
        } else {
          onSubmit();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const handleSelectSuggestion = useCallback((suggestion: Suggestion) => {
    setIsOpen(false);
    setHighlightedIndex(-1);
    onSelectSuggestion(suggestion);
  }, [onSelectSuggestion]);

  return (
    <div ref={wrapperRef} className={cn('flex-1', className)}>
      <div className="relative">
        {icon}
        <Input
          placeholder={placeholder}
          className={inputClassName}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              setIsOpen(true);
            }
          }}
          autoComplete="off"
        />
      </div>

      {isOpen && suggestions.length > 0 && (
        <div className={cn("absolute z-50 left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden", dropdownClassName)}>
          <ul className="max-h-60 overflow-auto">
            {suggestions.map((suggestion, index) => (
              <li
                key={`${suggestion.type}-${suggestion.value}-${index}`}
                className={cn(
                  'px-4 py-2 cursor-pointer transition-colors',
                  highlightedIndex === index
                    ? 'bg-blue-50 text-blue-900'
                    : 'hover:bg-gray-50 text-gray-900'
                )}
                onClick={() => handleSelectSuggestion(suggestion)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="flex items-center gap-3">
                  {/* Album artwork or fallback icon */}
                  {suggestion.type === 'song' && (
                    <div className="flex-shrink-0 w-12 h-12 rounded overflow-hidden bg-gray-100 flex items-center justify-center">
                      {suggestion.albumCoverUrl ? (
                        <img
                          src={suggestion.albumCoverUrl}
                          alt={suggestion.label}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Music className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                  )}

                  {/* Text content */}
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="font-medium truncate">{suggestion.label}</span>
                    <span className="text-xs text-gray-500 capitalize flex-shrink-0">
                      ({suggestion.type})
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {/* Hybrid Footer: Show total count */}
          {totalCount > 0 && value.trim().length >= 2 && (
            <div className="px-4 py-3 text-sm text-gray-700 bg-gray-50 border-t border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                 onClick={onSubmit}>
              <span className="font-medium">
                See all {totalCount.toLocaleString()} {indexName === 'displays' ? 'display' : 'sequence'}{totalCount !== 1 ? 's' : ''}
              </span>
              <span className="text-gray-500 ml-1">matching "{value}"</span>
            </div>
          )}

          {isLoading && (
            <div className="px-4 py-2 text-xs text-gray-500 border-t">
              Loading suggestions...
            </div>
          )}
        </div>
      )}
    </div>
  );
};
